import React, { useState, useEffect } from 'react';
import {
  Container, Box, Typography, Button, TextField, Alert,
  Paper, Divider, CircularProgress, Chip, Radio, RadioGroup,
  FormControlLabel, FormControl, FormLabel, Grid,
} from '@mui/material';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import { useParams, useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';

const API = process.env.REACT_APP_API_URL || 'https://watchpassion.vercel.app/api';
const stripePromise = process.env.REACT_APP_STRIPE_PUBLIC_KEY
  ? loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY)
  : null;

function StripeForm({ clientSecret, onSuccess }) {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setLoading(true);
    setError('');
    const { error: stripeError } = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: `${window.location.origin}/checkout/success` },
    });
    if (stripeError) setError(stripeError.message);
    setLoading(false);
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
      <PaymentElement />
      {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      <Button fullWidth type="submit" variant="contained" size="large"
        sx={{ mt: 3 }} disabled={!stripe || loading}>
        {loading ? <CircularProgress size={24} /> : 'Paga ora'}
      </Button>
    </Box>
  );
}

export default function CheckoutPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { authHeader, user } = useAuth();

  const [listing, setListing] = useState(null);
  const [loadingItem, setLoadingItem] = useState(true);
  const [paymentMethod, setPaymentMethod] = useState('stripe');
  const [discountCode, setDiscountCode] = useState('');
  const [discountInfo, setDiscountInfo] = useState(null);
  const [discountError, setDiscountError] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [paymentIntentId, setPaymentIntentId] = useState('');
  const [finalAmount, setFinalAmount] = useState(null);
  const [form, setForm] = useState({
    name: '', email: '', phone: '', street: '', civic: '', cap: '', city: '', province: '', country: 'Italia', notes: '',
  });

  useEffect(() => {
    if (user && user.role !== 'admin') {
      setForm(f => ({ ...f, name: user.name || '', email: user.email || '' }));
    }
  }, [user]);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const numericId = id?.split('-')[0];
    axios.get(`${API}/ebay/items/${numericId}`)
      .then(r => { setListing(r.data.data); setFinalAmount(r.data.data?.price); })
      .catch(() => {})
      .finally(() => setLoadingItem(false));
  }, [id]);

  const handleChange = (e) => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const applyDiscount = async () => {
    setDiscountError('');
    setDiscountInfo(null);
    if (!discountCode.trim()) return;
    try {
      const { data } = await axios.post(`${API}/orders/validate-discount`, {
        code: discountCode, amount: listing?.price,
      });
      setDiscountInfo(data);
      setFinalAmount(listing.price - data.discountAmount);
    } catch (err) {
      setDiscountError(err.response?.data?.error || 'Codice non valido');
    }
  };

  const createStripeIntent = async () => {
    if (!listing) return;
    const { data } = await axios.post(`${API}/payments/stripe/intent`, {
      amount: listing.price,
      itemId: listing.id,
      itemTitle: listing.title,
      discountCode: discountCode || undefined,
    });
    setClientSecret(data.clientSecret);
    setPaymentIntentId(data.paymentIntentId);
    setFinalAmount(data.finalAmount);
  };

  const handleBankTransfer = async () => {
    if (!form.email) return setError('Email richiesta');
    setLoading(true);
    setError('');
    try {
      await axios.post(`${API}/orders`, {
        itemId: listing.id, itemTitle: listing.title,
        itemImage: listing.image, itemUrl: listing.url,
        amount: finalAmount || listing.price,
        paymentMethod: 'bank_transfer',
        shippingName: form.name, shippingEmail: form.email,
        shippingPhone: form.phone,
        shippingAddress: `${form.street} ${form.civic}, ${form.cap} ${form.city} (${form.province}), ${form.country}`.trim(),
        discountCode: discountCode || undefined, notes: form.notes,
      }, { headers: authHeader() });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Errore creazione ordine');
    } finally {
      setLoading(false);
    }
  };

  const handleEbay = async () => {
    if (listing?.url) {
      await axios.post(`${API}/orders`, {
        itemId: listing.id, itemTitle: listing.title,
        itemImage: listing.image, itemUrl: listing.url,
        amount: listing.price, paymentMethod: 'ebay',
        shippingEmail: form.email || 'ebay@redirect',
      }, { headers: authHeader() }).catch(() => {});
      window.open(listing.url, '_blank', 'noopener,noreferrer');
    }
  };

  if (loadingItem) return <Container sx={{ pt: 8, textAlign: 'center' }}><CircularProgress /></Container>;
  if (!listing) return <Container sx={{ pt: 8 }}><Alert severity="error">Articolo non trovato</Alert></Container>;

  if (success) {
    return (
      <Container maxWidth="sm" sx={{ pt: 8, textAlign: 'center' }}>
        <Typography variant="h5" fontWeight="bold" gutterBottom>Ordine ricevuto!</Typography>
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          {paymentMethod === 'bank_transfer'
            ? `Effettua il bonifico di €${finalAmount?.toFixed(2)} a:\nIBAN: ${process.env.REACT_APP_IBAN || 'IT00 X000 0000 0000 0000 0000 000'}\nCausale: ${listing.title}`
            : 'Il tuo ordine è stato confermato.'}
        </Typography>
        <Button variant="contained" onClick={() => navigate('/mercato')}>Torna al marketplace</Button>
      </Container>
    );
  }

  const price = listing.price;
  const discount = discountInfo?.discountAmount || 0;
  const total = finalAmount ?? price;

  return (
    <Container maxWidth="md" sx={{ pt: 4, pb: 6 }}>
      <Typography variant="h5" fontWeight="bold" gutterBottom>Checkout</Typography>

      <Grid container spacing={3}>
        {/* Left: form */}
        <Grid item xs={12} md={7}>
          {/* Dati spedizione */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>I tuoi dati</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField fullWidth label="Nome e cognome *" name="name" value={form.name} onChange={handleChange} required />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Email *" name="email" type="email" value={form.email} onChange={handleChange} required />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Telefono" name="phone" value={form.phone} onChange={handleChange} />
              </Grid>
              <Grid item xs={8}>
                <TextField fullWidth label="Via / Piazza *" name="street" value={form.street} onChange={handleChange} required />
              </Grid>
              <Grid item xs={4}>
                <TextField fullWidth label="Civico *" name="civic" value={form.civic} onChange={handleChange} required />
              </Grid>
              <Grid item xs={4}>
                <TextField fullWidth label="CAP *" name="cap" value={form.cap} onChange={handleChange} required inputProps={{ maxLength: 5 }} />
              </Grid>
              <Grid item xs={5}>
                <TextField fullWidth label="Città *" name="city" value={form.city} onChange={handleChange} required />
              </Grid>
              <Grid item xs={3}>
                <TextField fullWidth label="Provincia" name="province" value={form.province} onChange={handleChange} inputProps={{ maxLength: 2 }} placeholder="es. AR" />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Nazione" name="country" value={form.country} onChange={handleChange} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Note per la spedizione" name="notes" value={form.notes} onChange={handleChange} multiline rows={2} />
              </Grid>
            </Grid>
          </Paper>

          {/* Codice sconto */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>Codice sconto</Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField fullWidth size="small" label="Codice" value={discountCode}
                onChange={e => setDiscountCode(e.target.value.toUpperCase())} />
              <Button variant="outlined" onClick={applyDiscount} startIcon={<LocalOfferIcon />}>
                Applica
              </Button>
            </Box>
            {discountError && <Alert severity="error" sx={{ mt: 1 }}>{discountError}</Alert>}
            {discountInfo && (
              <Alert severity="success" sx={{ mt: 1 }}>
                Sconto applicato: -{discountInfo.discount.type === 'percent'
                  ? `${discountInfo.discount.value}%`
                  : `€${discountInfo.discount.value}`} (−€{discount.toFixed(2)})
              </Alert>
            )}
          </Paper>

          {/* Metodo di pagamento */}
          <Paper sx={{ p: 3 }}>
            <FormControl>
              <FormLabel><Typography variant="h6">Metodo di pagamento</Typography></FormLabel>
              <RadioGroup value={paymentMethod} onChange={e => setPaymentMethod(e.target.value)}>
                <FormControlLabel value="stripe" control={<Radio />}
                  label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><CreditCardIcon fontSize="small" /> Carta di credito / debito</Box>} />
                <FormControlLabel value="bank_transfer" control={<Radio />}
                  label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><AccountBalanceIcon fontSize="small" /> Bonifico bancario</Box>} />
                <FormControlLabel value="ebay" control={<Radio />}
                  label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><OpenInNewIcon fontSize="small" /> Acquista su eBay</Box>} />
              </RadioGroup>
            </FormControl>

            <Box sx={{ mt: 2 }}>
              {paymentMethod === 'stripe' && stripePromise && (
                <>
                  {!clientSecret ? (
                    <Button fullWidth variant="contained" onClick={createStripeIntent}>
                      Procedi al pagamento con carta
                    </Button>
                  ) : (
                    <Elements stripe={stripePromise} options={{ clientSecret }}>
                      <StripeForm clientSecret={clientSecret} />
                    </Elements>
                  )}
                  {!stripePromise && <Alert severity="warning">Stripe non configurato</Alert>}
                </>
              )}

              {paymentMethod === 'stripe' && !stripePromise && (
                <Alert severity="warning">Pagamento con carta non ancora configurato.</Alert>
              )}

              {paymentMethod === 'bank_transfer' && (
                <Box>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Riceverai le coordinate bancarie via email. L'ordine verrà confermato alla ricezione del pagamento.
                  </Alert>
                  {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
                  <Button fullWidth variant="contained" onClick={handleBankTransfer} disabled={loading}>
                    {loading ? <CircularProgress size={24} /> : 'Conferma ordine (bonifico)'}
                  </Button>
                </Box>
              )}

              {paymentMethod === 'ebay' && (
                <Box>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Verrai reindirizzato all'annuncio eBay per completare l'acquisto.
                  </Alert>
                  <Button fullWidth variant="contained" onClick={handleEbay}
                    endIcon={<OpenInNewIcon />} disabled={!listing?.url}>
                    Vai su eBay
                  </Button>
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>

        {/* Right: riepilogo */}
        <Grid item xs={12} md={5}>
          <Paper sx={{ p: 3, position: 'sticky', top: 80 }}>
            <Typography variant="h6" gutterBottom>Riepilogo ordine</Typography>
            {listing.image && (
              <Box component="img" src={listing.image} alt={listing.title}
                sx={{ width: '100%', borderRadius: 1, mb: 2, maxHeight: 180, objectFit: 'cover' }} />
            )}
            <Typography variant="body1" fontWeight="bold" gutterBottom>{listing.title}</Typography>
            {listing.condition && <Typography variant="body2" color="text.secondary">{listing.condition}</Typography>}
            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography>Prezzo</Typography>
              <Typography>€{price.toFixed(2)}</Typography>
            </Box>
            {discount > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography color="success.main">Sconto</Typography>
                <Typography color="success.main">−€{discount.toFixed(2)}</Typography>
              </Box>
            )}
            <Divider sx={{ my: 1 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="h6">Totale</Typography>
              <Typography variant="h6" color="primary">€{total.toFixed(2)}</Typography>
            </Box>
            {listing.url && (
              <Button fullWidth variant="text" size="small" href={listing.url} target="_blank"
                sx={{ mt: 2 }} endIcon={<OpenInNewIcon />}>
                Vedi su eBay
              </Button>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
}
